import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAssetMeta, tagText, deviceName } from '../lib/assets';
import { contractorAuth } from '../lib/contractorAuth';
import '../components/Assets.css';

/**
 * LEAVERS (108, decision 5 - semi-manual). Gordon, 28 October: "a user
 * could change desk tomorrow or leave the company." Nothing happens to a
 * leaver's things by itself: this lists everybody who has been deactivated
 * and still holds devices or a fixed desk, and an IT person (or an IT
 * contractor) with write access sorts each one out by hand -
 *
 *   their desk      given to somebody else, or set back to unassigned or
 *                   to a hot desk, right here (its kit stays the desk's)
 *   their devices   each opened and taken back through its page's
 *                   ordinary Take back, which asks where it goes
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function AssetLeavers() {
  const meta = useAssetMeta();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [choice, setChoice] = useState({});
  const [problem, setProblem] = useState('');

  const load = useCallback(() => axios.get(`${API_BASE}/assets/leavers`, contractorAuth())
    .then((r) => { setData(r.data); setError(''); })
    .catch((e) => setError(e.response?.data?.error || 'Could not load the leavers.')), []);
  useEffect(() => { load(); }, [load]);

  const reassign = async (desk) => {
    const c = choice[desk.id] || '';
    if (!c) return;
    setProblem('');
    try {
      await axios.put(`${API_BASE}/assets/desks/${desk.id}`, c === 'hot' ? { desk_mode: 'hot' }
        : c === 'unassigned' ? { desk_mode: 'fixed', user_id: null } : { user_id: c.slice(2) }, contractorAuth());
      await load();
    } catch (e) {
      setProblem(e.response?.data?.error || 'That did not work.');
    }
  };

  return (
    <div className="container" data-testid="leavers-page">
      <p><Link to="/assets">← Assets</Link></p>
      <div className="page-header">
        <h1 className="page-title">Leavers</h1>
        <p className="page-description">
          People who have left and still have a desk or devices. Give each desk to somebody else or set it back to
          unassigned, and take each device back from its page.
        </p>
      </div>
      {error && <p className="as-error" role="alert">{error}</p>}
      {problem && <p className="as-error" role="alert">{problem}</p>}
      {data && data.leavers.length === 0 && <div className="empty-state"><p>Nobody who has left still has a desk or a device.</p></div>}
      {data && data.leavers.map((l) => (
        <section key={l.user.id} className="as-panel" aria-label={l.user.name} data-testid="leaver">
          <h2>{l.user.name}</h2>
          <p className="as-quiet">{l.user.email}</p>
          {/* Their leaver checklist (113), for an administrator: the same
              devices and desk, beside everything else leaving takes. */}
          {l.run && (
            <p data-leaver-run={l.run.id}>
              <Link to={`/people/runs/${l.run.id}`}>
                {l.run.status === 'open' ? 'Their leaver checklist →' : 'Their leaver checklist (closed) →'}
              </Link>
            </p>
          )}
          {l.desks.length > 0 && (
            <>
              <h3>{l.desks.length === 1 ? 'Their desk' : 'Their desks'}</h3>
              <ul className="as-tree">
                {l.desks.map((d) => (
                  <li key={d.id}>
                    <Link to={`/assets/desks/${d.id}`}><strong>{d.name}</strong></Link>
                    <span className="as-quiet">{` ${d.path}`}</span>
                    <span className="as-spacer" />
                    {d.write && (
                      <>
                        <select aria-label={`Give ${d.name} to`} value={choice[d.id] || ''}
                          onChange={(e) => setChoice((c) => ({ ...c, [d.id]: e.target.value }))}>
                          <option value="">Give it to…</option>
                          <option value="unassigned">Nobody yet (unassigned)</option>
                          <option value="hot">Make it a hot desk</option>
                          {(meta?.users || []).map((u) => <option key={u.id} value={`u:${u.id}`}>{u.display_name || u.email}</option>)}
                        </select>
                        <button type="button" className="btn btn-primary btn-small" disabled={!choice[d.id]} onClick={() => reassign(d)}>Save</button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {l.devices.length > 0 && (
            <>
              <h3>Devices to return</h3>
              <ul className="as-tree">
                {l.devices.map((a) => (
                  <li key={a.id}>
                    <Link to={`/assets/${a.id}`}><strong>{tagText(a)}</strong></Link>
                    <span className="as-quiet">{` ${a.category_name}${deviceName(a) !== a.category_name ? ` · ${deviceName(a)}` : ''}${a.location_path ? ` · ${a.location_path}` : ''}`}</span>
                    {a.is_personal && <span className="as-chip" data-tone="info">Personal</span>}
                    <span className="as-spacer" />
                    <Link className="btn btn-secondary btn-small" to={`/assets/${a.id}`}>Take back…</Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      ))}
    </div>
  );
}
